/**
 * OrderListPage - Main Order Search & Filter Page
 * Integrates all filter components with search results display
 * Features: URL-persisted filters, pagination, export, statistics
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Package,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';

// Import hooks
import { useOrderFilters } from './hooks/useOrderFilters';
import { useOrderSearch } from './hooks/useOrderSearch';

// Import components
import {
  FilterPanel,
  OrdersTable,
  ActiveFiltersDisplay,
  ResultsToolbar,
} from './components';

// Import types
import type { OrderSearchResultItem } from './types/filter.types';

// ============================================
// COMPONENT
// ============================================

export function OrderListPage(): JSX.Element {
  const navigate = useNavigate();

  // Filter state with URL synchronization
  const {
    filters,
    pagination,
    updateFilters,
    updatePagination,
    resetFilters,
    hasActiveFilters,
  } = useOrderFilters();

  // Search results from API
  const {
    orders,
    totalElements,
    totalPages,
    currentPage,
    pageSize,
    hasNext,
    hasPrevious,
    isLoading,
    error,
    refetch,
  } = useOrderSearch(filters, pagination);

  // Local state
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [sortField, setSortField] = useState<string>('orderDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // ============================================
  // STATISTICS CALCULATIONS
  // ============================================

  const statistics = React.useMemo(() => {
    const totalAmount = orders.reduce(
      (sum, order) => sum + order.totalAmount,
      0
    );
    const averageOrderValue =
      orders.length > 0 ? totalAmount / orders.length : 0;

    return {
      totalOrders: totalElements,
      totalAmount,
      averageOrderValue,
    };
  }, [orders, totalElements]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Handle filter removal from active filters display
   */
  const handleRemoveFilter = useCallback(
    (filterKey: keyof typeof filters, value?: unknown) => {
      if (filterKey === 'statuses' && value) {
        // Remove specific status from array
        const newStatuses = filters.statuses.filter((s) => s !== value);
        updateFilters({ statuses: newStatuses });
      } else if (filterKey === 'location' && value) {
        // Remove city or state
        if (value === 'city') {
          updateFilters({
            location: { ...filters.location, city: null },
          });
        } else if (value === 'state') {
          updateFilters({
            location: { ...filters.location, state: null },
          });
        }
      } else if (filterKey === 'orderDateRange') {
        updateFilters({ orderDateRange: { from: null, to: null } });
      } else if (filterKey === 'expectedDeliveryDateRange') {
        updateFilters({ expectedDeliveryDateRange: { from: null, to: null } });
      } else if (filterKey === 'actualDeliveryDateRange') {
        updateFilters({ actualDeliveryDateRange: { from: null, to: null } });
      } else if (filterKey === 'amountRange') {
        updateFilters({ amountRange: { min: null, max: null } });
      } else if (filterKey === 'searchTerm') {
        updateFilters({ searchTerm: '' });
      } else if (filterKey === 'customerType') {
        updateFilters({ customerType: 'ALL' });
      }
    },
    [filters, updateFilters]
  );

  /**
   * Handle view order action
   */
  const handleViewOrder = useCallback(
    (order: OrderSearchResultItem) => {
      navigate(`/sales/orders/${order.id}`);
    },
    [navigate]
  );

  /**
   * Handle edit order action
   */
  const handleEditOrder = useCallback(
    (order: OrderSearchResultItem) => {
      navigate(`/sales/orders/${order.id}/edit`);
    },
    [navigate]
  );

  /**
   * Handle delete order action
   */
  const handleDeleteOrder = useCallback((orderId: number) => {
    // TODO: Implement delete confirmation dialog and API call
    console.log('Delete order:', orderId);
  }, []);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      setSortField(field);
      setSortDirection(direction);
      // TODO: Implement backend sorting when API supports it
    },
    []
  );

  /**
   * Handle column header click for sorting
   */
  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        // Toggle direction
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        // New field, default to ascending
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField]
  );

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback(
    (value: string) => {
      updatePagination({ pageSize: parseInt(value, 10), page: 1 });
    },
    [updatePagination]
  );

  /**
   * Handle previous page
   */
  const handlePreviousPage = useCallback(() => {
    if (hasPrevious) {
      updatePagination({ page: pagination.page - 1 });
    }
  }, [hasPrevious, pagination.page, updatePagination]);

  /**
   * Handle next page
   */
  const handleNextPage = useCallback(() => {
    if (hasNext) {
      updatePagination({ page: pagination.page + 1 });
    }
  }, [hasNext, pagination.page, updatePagination]);

  /**
   * Handle jump to page
   */
  const handleJumpToPage = useCallback(
    (pageNumber: number) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        updatePagination({ page: pageNumber });
      }
    },
    [totalPages, updatePagination]
  );

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Format currency for display
   */
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">
            Search and manage all customer orders
          </p>
        </div>
        <Button
          onClick={() => navigate('/sales/orders/new')}
          className="bg-green-600 hover:bg-green-700"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Orders
            </CardTitle>
            <Package className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
              ) : (
                statistics.totalOrders.toLocaleString('en-IN')
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {hasActiveFilters ? 'Filtered results' : 'All orders'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Amount
            </CardTitle>
            <DollarSign className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
              ) : (
                formatCurrency(statistics.totalAmount)
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {hasActiveFilters ? 'Filtered orders' : 'All orders'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Average Order Value
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
              ) : (
                formatCurrency(statistics.averageOrderValue)
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Per order in current view
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Panel */}
      <Card>
        <CardContent className="pt-6">
          <FilterPanel filters={filters} onFiltersChange={updateFilters} />
        </CardContent>
      </Card>

      {/* Active Filters Display */}
      <ActiveFiltersDisplay
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={resetFilters}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Results Section */}
      <Card>
        {/* Results Toolbar */}
        <ResultsToolbar
          totalElements={totalElements}
          currentPage={currentPage}
          pageSize={pageSize}
          orders={orders}
          isLoading={isLoading}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onRefresh={refetch}
          sortBy={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
        />

        {/* Orders Table */}
        <CardContent className="p-0">
          <OrdersTable
            orders={orders}
            isLoading={isLoading}
            onViewOrder={handleViewOrder}
            onEditOrder={handleEditOrder}
            onDeleteOrder={handleDeleteOrder}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 border-t">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={!hasPrevious || isLoading}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasNext || isLoading}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default OrderListPage;
